import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { api, TRY } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, Wallet, ListChecks, CalendarClock, ArrowUpRight, ArrowDownRight, ShieldCheck, Zap } from "lucide-react";
import { Link } from "react-router-dom";

function groupData(transactions, range) {
  const buckets = {};
  const fmt = (d) => {
    const date = new Date(d);
    if (range === "weekly") {
      const onejan = new Date(date.getFullYear(), 0, 1);
      const week = Math.ceil(((date - onejan) / 86400000 + onejan.getDay() + 1) / 7);
      return `${date.getFullYear()} H${week}`;
    }
    if (range === "monthly") {
      return date.toLocaleDateString("tr-TR", { month: "short", year: "2-digit" });
    }
    return `${date.getFullYear()}`;
  };
  transactions.forEach((t) => {
    const key = fmt(t.date);
    if (!buckets[key]) buckets[key] = { name: key, Gelir: 0, Gider: 0 };
    if (t.type === "income") buckets[key].Gelir += Number(t.amount);
    else buckets[key].Gider += Number(t.amount);
  });
  return Object.values(buckets).slice(-8);
}

const KPI = ({ icon: Icon, label, value, subtext, accent, badgeText, badgeColor, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Card className="glass-card glass-card-hover p-6 rounded-2xl relative overflow-hidden flex flex-col justify-between h-full">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <div className={`p-2.5 rounded-xl ${accent} border border-white/5`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-4">
        <p className="font-anton text-3xl sm:text-4xl text-white tracking-wide">{value}</p>
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-slate-400">{subtext}</p>
          {badgeText && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badgeColor}`}>
              {badgeText}
            </span>
          )}
        </div>
      </div>
    </Card>
  </motion.div>
);

export default function Overview() {
  const [tx, setTx] = useState([]);
  const [slots, setSlots] = useState([]);
  const [todos, setTodos] = useState([]);
  const [range, setRange] = useState("monthly");

  useEffect(() => {
    Promise.all([api.get("/transactions"), api.get("/slots"), api.get("/todos")]).then(
      ([a, b, c]) => {
        setTx(a.data);
        setSlots(b.data);
        setTodos(c.data);
      }
    );
  }, []);

  const income = tx.filter((t) => t.type === "income").reduce((s, t) => s + Number(t.amount), 0);
  const expense = tx.filter((t) => t.type === "expense").reduce((s, t) => s + Number(t.amount), 0);
  const netBalance = income - expense;
  const today = new Date().toISOString().slice(0, 10);
  const emptyToday = slots.filter((s) => s.date === today && s.status === "empty").length;
  const openTodos = todos.filter((t) => !t.completed).length;
  const chartData = groupData(tx, range);

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400">10-11 Operasyon Kontrolü</p>
          </div>
          <h1 className="font-anton text-4xl sm:text-5xl tracking-wide gradient-text">GENEL BAKIŞ VE FİNANS</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/slotlar" className="px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 transition-all flex items-center gap-1.5">
            <CalendarClock className="w-4 h-4" /> Slot Takibine Git
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI
          icon={TrendingUp}
          label="Toplam Gelir"
          value={TRY(income)}
          subtext="Girilen tüm gelir kalemleri"
          accent="bg-emerald-500/10 text-emerald-400"
          badgeText="+ Canlı"
          badgeColor="bg-emerald-500/20 text-emerald-400"
          delay={0}
        />
        <KPI
          icon={TrendingDown}
          label="Toplam Gider"
          value={TRY(expense)}
          subtext="Girilen tüm gider kalemleri"
          accent="bg-rose-500/10 text-rose-400"
          badgeText="Operasyonel"
          badgeColor="bg-rose-500/20 text-rose-400"
          delay={0.05}
        />
        <KPI
          icon={Wallet}
          label="Net Bakiye"
          value={TRY(netBalance)}
          subtext="Gelir - Gider Farkı"
          accent={netBalance >= 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"}
          badgeText={netBalance >= 0 ? "Pozitif" : "Negatif"}
          badgeColor={netBalance >= 0 ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}
          delay={0.1}
        />
        <KPI
          icon={ListChecks}
          label="Açık Görevler"
          value={`${openTodos} İş`}
          subtext="Tamamlanmayı bekleyen"
          accent="bg-blue-500/10 text-blue-400"
          badgeText="Aktif"
          badgeColor="bg-blue-500/20 text-blue-400"
          delay={0.15}
        />
      </div>

      <Card className="glass-card p-6 rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="font-anton text-2xl tracking-wide text-white">GELİR / GİDER KARŞILAŞTIRMA ANALİZİ</h2>
            <p className="text-xs text-slate-400 mt-0.5">Dönemsel finansal grafik raporları</p>
          </div>
          <Tabs value={range} onValueChange={setRange}>
            <TabsList className="bg-slate-900/80 border border-slate-800 p-1 rounded-xl">
              <TabsTrigger value="weekly" data-testid="range-weekly" className="text-xs rounded-lg">Haftalık</TabsTrigger>
              <TabsTrigger value="monthly" data-testid="range-monthly" className="text-xs rounded-lg">Aylık</TabsTrigger>
              <TabsTrigger value="yearly" data-testid="range-yearly" className="text-xs rounded-lg">Yıllık</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="h-80">
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm">
              Henüz veri yok. Gelir/Gider sayfasından kayıt ekleyin.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 12, boxShadow: "0 10px 25px rgba(0,0,0,0.5)" }}
                  formatter={(v) => TRY(v)}
                />
                <Legend wrapperStyle={{ paddingTop: 12 }} />
                <Bar dataKey="Gelir" fill="#10b981" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Gider" fill="#f43f5e" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-5">
        <Card className="glass-card glass-card-hover p-6 rounded-2xl flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-5 h-5 text-emerald-400" />
              <h3 className="font-semibold text-slate-200">Bugün Müsait Slotlar</h3>
            </div>
            <p className="font-anton text-4xl text-emerald-400">{emptyToday}</p>
            <p className="text-xs text-slate-400 mt-1">{today} tarihi için boş slot sayısı</p>
          </div>
          <Link to="/slotlar" className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors">
            <Zap className="w-6 h-6" />
          </Link>
        </Card>

        <Card className="glass-card glass-card-hover p-6 rounded-2xl flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ListChecks className="w-5 h-5 text-blue-400" />
              <h3 className="font-semibold text-slate-200">Görev Tamamlanma Oranı</h3>
            </div>
            <p className="font-anton text-4xl text-blue-400">
              {todos.filter((t) => t.completed).length} / {todos.length}
            </p>
            <p className="text-xs text-slate-400 mt-1">Son görev ilerleme durumu</p>
          </div>
          <Link to="/gorevler" className="p-3 rounded-2xl bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors">
            <ShieldCheck className="w-6 h-6" />
          </Link>
        </Card>
      </div>
    </div>
  );
}

function Sparkles(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.937A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z"/>
    </svg>
  );
}

